import { Component, computed, inject, input, model, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Select } from 'primeng/select';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { CompanyService } from '@/app/services/company.service';
import { AuthService } from '@/app/services/auth.service';
import { PaymentMethod, RealmConfig, RealmFineraConfig, RealmFineraWithdrawConfig } from '@/lib-backend';
import { FineraTab } from './finera-tab';
import { StripeTab } from './stripe-tab';

interface PaymentMethodOption {
    label: string;
    value: PaymentMethod;
    disabled: boolean;
    hint: string | null;
}

@Component({
    selector: 'app-website-info-payment',
    standalone: true,
    imports: [
        FormsModule, Tabs, Tab, TabList, TabPanels, TabPanel,
        Select, Tag, Tooltip,
        FineraTab, StripeTab,
    ],
    templateUrl: './payment-tab.html',
})
export class PaymentTab implements OnInit {
    private readonly companyService = inject(CompanyService);
    private readonly authService = inject(AuthService);

    readonly realm = input.required<RealmConfig>();
    readonly fineraDraft = model.required<RealmFineraConfig>();
    readonly fineraWithdrawDraft = model.required<RealmFineraWithdrawConfig>();
    readonly paymentMethodDraft = model.required<PaymentMethod>();

    protected readonly company = computed(() =>
        this.companyService.byId().get(this.realm().CompanyId) ?? null
    );

    protected readonly fineraReady = computed(() => this.realm().Finera.IsEnabled);
    protected readonly stripeReady = computed(() => this.company()?.Stripe.IsEnabled === true);

    protected readonly paymentMethodOptions = computed<PaymentMethodOption[]>(() => [
        {
            label: 'None — payments disabled',
            value: PaymentMethod.None,
            disabled: false,
            hint: null,
        },
        {
            label: 'Finera',
            value: PaymentMethod.Finera,
            disabled: !this.fineraReady(),
            hint: this.fineraReady() ? null : 'Enable Finera below first.',
        },
        {
            label: 'Stripe',
            value: PaymentMethod.Stripe,
            disabled: !this.stripeReady(),
            hint: this.stripeReady() ? null : 'Enable Stripe in the parent company first.',
        },
    ]);

    protected readonly methodDirty = computed(() =>
        this.paymentMethodDraft() !== this.realm().ActivePaymentMethod
    );

    /** Initial subtab — show whichever provider matches the active method, default Finera. */
    protected readonly initialSubtab = computed(() =>
        this.paymentMethodDraft() === PaymentMethod.Stripe ? 'stripe' : 'finera'
    );

    ngOnInit(): void {
        // Stripe preview reads from Company.Stripe; ensure companies are loaded.
        if (this.authService.isGlobalAdmin() && this.companyService.companies().length === 0) {
            this.companyService.loadCompanies();
        }
    }

    protected setMethod(value: PaymentMethod): void {
        this.paymentMethodDraft.set(value);
    }

    protected resetMethod(): void {
        this.paymentMethodDraft.set(this.realm().ActivePaymentMethod);
    }
}
